/**
 * FloatingPlanWindow - Fenêtre flottante, déplaçable, redimensionnable pour afficher le plan du stade
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Maximize2, Minimize2, MapPin } from 'lucide-react';

const FloatingPlanWindow = ({ stadium, matchInfo, onClose }) => {
  const [pos, setPos] = useState({ x: typeof window !== 'undefined' ? window.innerWidth - 620 : 400, y: 80 });
  const [size, setSize] = useState({ w: 580, h: 450 });
  const [minimized, setMinimized] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const windowRef = useRef(null);

  const mapUrl = stadium?.mapUrl || '';

  // Drag handlers
  const onDragStart = useCallback((e) => {
    if (e.target.closest('.no-drag')) return;
    setDragging(true);
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  }, [pos]);

  // Resize handlers
  const onResizeStart = useCallback((e) => {
    setResizing(true);
    dragOffset.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };
    e.preventDefault();
    e.stopPropagation();
  }, [size]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (dragging) {
        setPos({
          x: Math.max(0, Math.min(window.innerWidth - 200, e.clientX - dragOffset.current.x)),
          y: Math.max(0, Math.min(window.innerHeight - 60, e.clientY - dragOffset.current.y))
        });
      }
      if (resizing) {
        const dw = e.clientX - dragOffset.current.x;
        const dh = e.clientY - dragOffset.current.y;
        setSize({
          w: Math.max(300, dragOffset.current.w + dw),
          h: Math.max(200, dragOffset.current.h + dh)
        });
      }
    };
    const onMouseUp = () => {
      setDragging(false);
      setResizing(false);
    };
    if (dragging || resizing) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragging, resizing]);

  if (!stadium) return null;

  return (
    <div
      ref={windowRef}
      className="fixed z-[9999] shadow-2xl rounded-lg border border-[#C4A052]/40 overflow-hidden bg-zinc-900 flex flex-col"
      style={{
        left: pos.x,
        top: pos.y,
        width: minimized ? 280 : size.w,
        height: minimized ? 40 : size.h,
        transition: dragging || resizing ? 'none' : 'width 0.2s, height 0.2s',
        cursor: dragging ? 'grabbing' : 'default',
      }}
      data-testid="floating-plan-window"
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Title bar - draggable */}
      <div
        className="flex items-center justify-between px-3 py-1.5 bg-zinc-800 border-b border-[#C4A052]/30 cursor-grab select-none flex-shrink-0"
        onMouseDown={onDragStart}
        style={{ cursor: dragging ? 'grabbing' : 'grab' }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="h-3.5 w-3.5 text-[#C4A052] flex-shrink-0" />
          <span className="text-xs text-white font-medium truncate">
            {matchInfo?.matchNum && <span className="text-[#C4A052] mr-1">{matchInfo.matchNum}</span>}
            {stadium.name}
          </span>
        </div>
        <div className="flex items-center gap-1 no-drag flex-shrink-0">
          <button
            onClick={() => setMinimized(!minimized)}
            className="h-5 w-5 flex items-center justify-center rounded hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
          >
            {minimized ? <Maximize2 className="h-3 w-3" /> : <Minimize2 className="h-3 w-3" />}
          </button>
          <button
            onClick={onClose}
            className="h-5 w-5 flex items-center justify-center rounded hover:bg-red-500/30 text-zinc-400 hover:text-red-400 transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Content */}
      {!minimized && (
        <>
          <div className="flex-1 overflow-auto p-1 flex items-center justify-center bg-black/50">
            {mapUrl ? (
              <img
                src={mapUrl}
                alt={`Plan ${stadium.name}`}
                className="max-w-full max-h-full object-contain"
                draggable={false}
              />
            ) : (
              <span className="text-zinc-500 text-sm">Aucun plan disponible</span>
            )}
          </div>

          {/* Resize handle */}
          <div
            className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
            onMouseDown={onResizeStart}
            style={{ background: 'linear-gradient(135deg, transparent 50%, #C4A052 50%)' }}
          />
        </>
      )}
    </div>
  );
};

export default FloatingPlanWindow;
